import React, { useState } from 'react';
import { ArrowLeftIcon, TruckIcon, StoreIcon, CheckCircleIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { CartItem, DeliveryMethod } from '../types';
interface CheckoutProps {
  cart: CartItem[];
  onNavigateBack: () => void;
  onNavigateToProfile: () => void;
  onPlaceOrder: () => void;
}
export function Checkout({
  cart,
  onNavigateBack,
  onNavigateToProfile,
  onPlaceOrder
}: CheckoutProps) {
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>('delivery');
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const STIPEND_TOTAL = 200;
  const TAX_RATE = 0.085;
  const coveredItems = cart.filter(item => item.isCovered);
  const extraItems = cart.filter(item => !item.isCovered);
  const coveredSubtotal = coveredItems.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const coveredTax = coveredSubtotal * TAX_RATE;
  const coveredTotal = coveredSubtotal + coveredTax;
  const stipendUsed = Math.min(coveredTotal, STIPEND_TOTAL);
  const extraSubtotal = extraItems.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const extraTax = extraSubtotal * TAX_RATE;
  const deliveryFee = deliveryMethod === 'delivery' ? 4.99 : 0;
  const userPays = extraSubtotal + extraTax + deliveryFee;
  const lolaFee = coveredSubtotal > 0 ? coveredSubtotal * 0.05 : 0;
  const handlePlaceOrder = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setOrderPlaced(true);
      setTimeout(() => {
        onPlaceOrder();
      }, 2000);
    }, 1500);
  };
  if (orderPlaced) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-id="element-162">
        <div className="text-center max-w-md" data-id="element-163">
          <div className="bg-green-500 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6" data-id="element-164">
            <CheckCircleIcon className="w-12 h-12 text-white" data-id="element-165" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-166">
            Order Placed! 🎉
          </h1>
          <p className="text-gray-600 mb-2" data-id="element-167">Your groceries are on the way.</p>
          <p className="text-sm text-gray-500" data-id="element-168">
            Your RD has been notified of your plan-approved choices.
          </p>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-gray-50" data-id="element-169">
      <Header cartCount={cartCount} onCartClick={onNavigateBack} onProfileClick={onNavigateToProfile} data-id="element-170" />

      <div className="max-w-lg mx-auto px-4 py-6" data-id="element-171">
        <button onClick={onNavigateBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors" data-id="element-172">
          <ArrowLeftIcon className="w-5 h-5" data-id="element-173" />
          <span className="font-medium" data-id="element-174">Back to Cart</span>
        </button>

        <h1 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-175">Checkout</h1>

        <div className="mb-6" data-id="element-176">
          <h2 className="text-lg font-bold text-gray-900 mb-3" data-id="element-177">
            Delivery Method
          </h2>

          <div className="space-y-3" data-id="element-178">
            <button onClick={() => setDeliveryMethod('delivery')} className={`w-full p-4 rounded-2xl border-2 transition-all ${deliveryMethod === 'delivery' ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-179">
              <div className="flex items-center gap-3" data-id="element-180">
                <div className={`p-2 rounded-full ${deliveryMethod === 'delivery' ? 'bg-green-500' : 'bg-gray-200'}`} data-id="element-181">
                  <TruckIcon className={`w-6 h-6 ${deliveryMethod === 'delivery' ? 'text-white' : 'text-gray-600'}`} data-id="element-182" />
                </div>
                <div className="flex-1 text-left" data-id="element-183">
                  <p className="font-semibold text-gray-900" data-id="element-184">Delivery</p>
                  <p className="text-sm text-gray-600" data-id="element-185">
                    Get it delivered to your door
                  </p>
                </div>
                <p className="font-bold text-gray-900" data-id="element-186">$4.99</p>
              </div>
            </button>

            <button onClick={() => setDeliveryMethod('pickup')} className={`w-full p-4 rounded-2xl border-2 transition-all ${deliveryMethod === 'pickup' ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-187">
              <div className="flex items-center gap-3" data-id="element-188">
                <div className={`p-2 rounded-full ${deliveryMethod === 'pickup' ? 'bg-green-500' : 'bg-gray-200'}`} data-id="element-189">
                  <StoreIcon className={`w-6 h-6 ${deliveryMethod === 'pickup' ? 'text-white' : 'text-gray-600'}`} data-id="element-190" />
                </div>
                <div className="flex-1 text-left" data-id="element-191">
                  <p className="font-semibold text-gray-900" data-id="element-192">Pickup</p>
                  <p className="text-sm text-gray-600" data-id="element-193">
                    Pick up at your local store
                  </p>
                </div>
                <p className="font-bold text-green-600" data-id="element-194">Free</p>
              </div>
            </button>
          </div>
        </div>

        {deliveryMethod === 'delivery' && <div className="mb-6" data-id="element-195">
            <h2 className="text-lg font-bold text-gray-900 mb-3" data-id="element-196">
              Delivery Address
            </h2>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4" data-id="element-197">
              <p className="font-medium text-gray-900" data-id="element-198">123 Main Street</p>
              <p className="text-sm text-gray-600" data-id="element-199">Apt 4B</p>
              <p className="text-sm text-gray-600" data-id="element-200">San Francisco, CA 94102</p>
            </div>
          </div>}

        {deliveryMethod === 'pickup' && <div className="mb-6" data-id="element-201">
            <h2 className="text-lg font-bold text-gray-900 mb-3" data-id="element-202">
              Pickup Location
            </h2>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4" data-id="element-203">
              <p className="font-medium text-gray-900" data-id="element-204">Whole Foods Market</p>
              <p className="text-sm text-gray-600" data-id="element-205">450 Rhode Island St</p>
              <p className="text-sm text-gray-600" data-id="element-206">San Francisco, CA 94107</p>
            </div>
          </div>}

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 mb-6" data-id="element-207">
          <h2 className="text-lg font-bold text-gray-900 mb-3" data-id="element-208">
            Order Summary
          </h2>

          <div className="space-y-2 mb-3" data-id="element-209">
            {coveredSubtotal > 0 && <>
                <div className="flex justify-between text-sm" data-id="element-210">
                  <span className="text-green-600 font-medium" data-id="element-211">
                    Covered Items (subtotal)
                  </span>
                  <span className="font-medium text-green-600" data-id="element-212">
                    ${coveredSubtotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-213">
                  <span className="text-green-600" data-id="element-214">Tax on Covered Items</span>
                  <span className="font-medium text-green-600" data-id="element-215">
                    ${coveredTax.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm pb-2 border-b border-gray-200" data-id="element-216">
                  <span className="text-green-600 font-semibold" data-id="element-217">
                    Covered by Healthcare Plan
                  </span>
                  <span className="font-semibold text-green-600" data-id="element-218">
                    ${stipendUsed.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm text-gray-500 pb-2 border-b border-gray-200" data-id="element-219">
                  <span data-id="element-220">Lola Fee (paid by plan)</span>
                  <span data-id="element-221">${lolaFee.toFixed(2)}</span>
                </div>
              </>}
            {extraSubtotal > 0 && <>
                <div className="flex justify-between text-sm pt-2" data-id="element-222">
                  <span className="text-gray-600" data-id="element-223">Extra Items</span>
                  <span className="font-medium text-gray-900" data-id="element-224">
                    ${extraSubtotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-225">
                  <span className="text-gray-600" data-id="element-226">Tax on Extra Items</span>
                  <span className="font-medium text-gray-900" data-id="element-227">
                    ${extraTax.toFixed(2)}
                  </span>
                </div>
              </>}
            {deliveryMethod === 'delivery' && <div className="flex justify-between text-sm" data-id="element-228">
                <span className="text-gray-600" data-id="element-229">Delivery Fee</span>
                <span className="font-medium text-gray-900" data-id="element-230">
                  ${deliveryFee.toFixed(2)}
                </span>
              </div>}
          </div>

          <div className="border-t border-gray-200 pt-3" data-id="element-231">
            <div className="flex justify-between mb-2" data-id="element-232">
              <span className="font-bold text-gray-900" data-id="element-233">You Pay</span>
              <span className="font-bold text-xl text-gray-900" data-id="element-234">
                ${userPays.toFixed(2)}
              </span>
            </div>
            {stipendUsed > 0 && <p className="text-xs text-green-600" data-id="element-235">
                ${stipendUsed.toFixed(2)} covered by your healthcare plan
              </p>}
          </div>
        </div>

        <button onClick={handlePlaceOrder} disabled={isProcessing} className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white font-bold py-4 px-6 rounded-full text-lg shadow-lg transition-colors" data-id="element-236">
          {isProcessing ? 'Processing...' : 'Place Order'}
        </button>

        <p className="text-center text-xs text-gray-500 mt-3" data-id="element-237">
          Powered by Instacart • Your RD will receive a summary of this order
        </p>
      </div>
    </div>;
}